import { Injectable, OnDestroy } from '@angular/core';
import { MessageHttpService } from './message.http.service';
import { IMessage, IMessageRedirectAction } from 'src/app/models';
import { images } from 'src/images';
import { IMessagesFilters } from 'src/app/filters';
import { CommunicationUserType, MessageStatus, ModalQueryParam } from 'src/app/models/enums';
import { BehaviorSubject, lastValueFrom, Subscription, interval } from 'rxjs';
import { objectToCamel } from 'ts-case-convert';
import { initialPaginationState } from 'src/app/store/initialisation/pagination';
import { IPushNotification, IPushNotificationResponse } from 'src/app/models/pushNotification';
import { RedirectType } from 'src/app/models/enums/redirectType';
import { Params, Router } from '@angular/router';

export interface IPushNotificationTarget {
  path: string;
  queryParams: Params;
}

@Injectable({
  providedIn: 'root',
})
export class MessageService implements OnDestroy {
  private pollingSubscription: Subscription | null = null;
  private readonly POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes

  private notificationsSubject = new BehaviorSubject<IPushNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  private readonly pushNotificationRoutes: Record<string, string> = {
    [RedirectType.USER]: '/users',
    [RedirectType.DRIVER]: '/drivers',
    [RedirectType.BOOKING]: '/bookings',
    [RedirectType.TRIP]: '/trips',
    [RedirectType.MESSAGE]: '/messages/users',
    [RedirectType.SUGGESTION]: '/suggestions/users',
    [RedirectType.OBSERVATION]: '/suggestions/drivers',
    [RedirectType.SUGGESTION_COMMENT]: '/suggestions/users',
    [RedirectType.OBSERVATION_COMMENT]: '/suggestions/drivers',
  };

  private readonly incidentRedirectAction: IMessageRedirectAction = {
    icon: images.suggestions.suggestionsIcon,
    labelKey: 'messages.redirect.incident',
  };

  private readonly redirectTargetActions: Record<string, IMessageRedirectAction> = {
    [RedirectType.USER]: { icon: images.messages.user, labelKey: 'messages.redirect.user' },
    [RedirectType.DRIVER]: { icon: images.messages.driver, labelKey: 'messages.redirect.driver' },
    [RedirectType.BOOKING]: { icon: images.sidebar.bookings, labelKey: 'messages.redirect.booking' },
    [RedirectType.TRIP]: { icon: images.sidebar.trips, labelKey: 'messages.redirect.trip' },
    [RedirectType.MESSAGE]: { icon: images.suggestions.messagesIcon, labelKey: 'messages.redirect.message' },
    [RedirectType.SUGGESTION]: this.incidentRedirectAction,
    [RedirectType.OBSERVATION]: this.incidentRedirectAction,
    [RedirectType.SUGGESTION_COMMENT]: this.incidentRedirectAction,
    [RedirectType.OBSERVATION_COMMENT]: this.incidentRedirectAction,
  };

  private readonly commentRedirectTypes = new Set<RedirectType>([
    RedirectType.SUGGESTION_COMMENT,
    RedirectType.OBSERVATION_COMMENT,
  ]);

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private messageHttpService: MessageHttpService,
    private router: Router,
  ) {}

  ngOnDestroy() {
    this.stopPolling();
  }

  startPolling() {
    this.fetchPushNotifications();

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.fetchPushNotifications();
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  async fetchPushNotifications(page: number = 1, size: number = 20) {
    this.loadingSubject.next(true);
    try {
      const source$ = this.messageHttpService.getPushNotifications(page, size);
      const response = await lastValueFrom(source$);
      const data = objectToCamel(response as object) as { data: IPushNotificationResponse };
      const notifications = data.data?.content || [];
      this.notificationsSubject.next(notifications);
      await this.fetchUnreadCount();
      return notifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    } finally {
      this.loadingSubject.next(false);
    }
  }

  private async fetchUnreadCount() {
    const response = await lastValueFrom(this.messageHttpService.getUnreadPushNotificationsCount());
    this.unreadCountSubject.next(Number(response) || 0);
  }

  private shiftUnreadCount(delta: number) {
    this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value + delta));
  }

  async markPushNotificationAsRead(id: number) {
    try {
      const source$ = this.messageHttpService.markPushNotificationAsRead(id);
      await lastValueFrom(source$);

      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = currentNotifications.map(n =>
        n.id === id ? { ...n, read: true } : n
      );
      this.notificationsSubject.next(updatedNotifications);
      this.shiftUnreadCount(-1);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  async markPushNotificationAsUnread(id: number) {
    try {
      const source$ = this.messageHttpService.markPushNotificationAsUnread(id);
      await lastValueFrom(source$);

      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = currentNotifications.map(n =>
        n.id === id ? { ...n, read: false } : n
      );
      this.notificationsSubject.next(updatedNotifications);
      this.shiftUnreadCount(1);
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  }

  async togglePushNotificationReadStatus(notification: IPushNotification) {
    if (notification.read) {
      await this.markPushNotificationAsUnread(notification.id);
    } else {
      await this.markPushNotificationAsRead(notification.id);
    }
  }

  async markAllPushNotificationsAsRead() {
    try {
      const source$ = this.messageHttpService.markAllPushNotificationsAsRead();
      await lastValueFrom(source$);

      const currentNotifications = this.notificationsSubject.value;
      const updatedNotifications = currentNotifications.map(n => ({ ...n, read: true }));
      this.notificationsSubject.next(updatedNotifications);
      this.unreadCountSubject.next(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  normalizeRedirectType(type: RedirectType | string | null | undefined): RedirectType | null {
    const comparableType = this.toComparableRedirectType(type);
    if (!comparableType) return null;
    return Object.values(RedirectType).find(
      (redirectType) => this.toComparableRedirectType(redirectType) === comparableType,
    ) ?? null;
  }

  private toComparableRedirectType(type: RedirectType | string | null | undefined): string {
    return (type ?? '').toString().replace(/_/g, '').toUpperCase();
  }

  getPushNotificationTarget(
    redirectType: RedirectType | string | null | undefined,
    redirectId: number | string | null | undefined,
  ): IPushNotificationTarget | null {
    const normalizedType = this.normalizeRedirectType(redirectType);
    if (!redirectId || !normalizedType) return null;
    const baseRoute = this.pushNotificationRoutes[normalizedType];
    if (!baseRoute) return null;
    const isCommentType = this.commentRedirectTypes.has(normalizedType);
    return {
      path: `${baseRoute}/${redirectId}`,
      queryParams: { [ModalQueryParam.CONVERSATION]: isCommentType ? '' : null, t: Date.now() },
    };
  }

  getRedirectTargetAction(
    redirectType: RedirectType | string | null | undefined,
    redirectId: number | string | null | undefined,
  ): IMessageRedirectAction | null {
    const normalizedType = this.normalizeRedirectType(redirectType);
    if (!redirectId || !normalizedType || !this.pushNotificationRoutes[normalizedType]) return null;
    return this.redirectTargetActions[normalizedType] ?? null;
  }

  getInitialDeliveryStatus(message: IMessage | null | undefined): MessageStatus {
    return this.getDeliveryStatus(message) === MessageStatus.FAILED ? MessageStatus.FAILED : MessageStatus.SUCCESS;
  }

  getDeliveryStatus(message: IMessage | null | undefined): MessageStatus {
    return message?.deliveryStatus ?? (message?.successful ? MessageStatus.SUCCESS : MessageStatus.FAILED);
  }

  getRedirectTargetUrl(
    redirectType: RedirectType | string | null | undefined,
    redirectId: number | string | null | undefined,
  ): string | null {
    const target = this.getPushNotificationTarget(redirectType, redirectId);
    if (!target) return null;
    return this.router.serializeUrl(this.router.createUrlTree([target.path], { queryParams: target.queryParams }));
  }

  openRedirectTarget(
    redirectType: RedirectType | string | null | undefined,
    redirectId: number | string | null | undefined,
  ): boolean {
    const target = this.getPushNotificationTarget(redirectType, redirectId);
    if (!target) return false;
    this.router.navigate([target.path], { queryParams: target.queryParams });
    return true;
  }

  getPushNotifications() {
    return this.notificationsSubject.value;
  }

  getUnreadCount() {
    return this.unreadCountSubject.value;
  }

  async createMessage(message: IMessage, userType: CommunicationUserType) {
    const source$ = this.messageHttpService.createMessage(message, userType);
    const response = await lastValueFrom(source$);
    const messageCreated = objectToCamel(response as object);
    return messageCreated;
  }

  async getMessages(type: CommunicationUserType, page?: number, size?: number) {
    const source$ = this.messageHttpService.getMessages(
      type,
      page,
      size
    );
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getData(messages);
    return messagesArray;
  }

  async filterMessages(
    communicationUserType: CommunicationUserType,
    page: number,
    size: number,
    filters: IMessagesFilters,
    searchValue?: string
  ) {
    const source$ = this.messageHttpService.filter(
      communicationUserType,
      page,
      size,
      filters,
      searchValue
    );
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getData(messages);
    return messagesArray;
  }

  async getSenders(userType: CommunicationUserType) {
    const source$ = this.messageHttpService.getSenders(userType);
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    return messages;
  }

  async getMessageById(id: number): Promise<IMessage> {
    const source$ = this.messageHttpService.getMessageById(id);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object) as IMessage;
  }

  async getMessageDetails(id: number, page: number, size: number, status: MessageStatus, searchValue?: string) {
    const source$ = this.messageHttpService.getMessageDetail(id, page, size, status, searchValue);
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getDataWithPagination(messages, page);
    return messagesArray;
  }

  async getMessageReceivers(message: IMessage, userType: CommunicationUserType, page?: number, size?: number) {
    const source$ = this.messageHttpService.getMessageReceivers(message, userType, page, size);
    const response = await lastValueFrom(source$);
    const messages = objectToCamel(response as object);
    const messagesArray = this.getDataWithPagination(messages, page || 0);
    return messagesArray;
  }

  getData(response: any) {
    return response.data;
  }

  getDataWithPagination(response: any, page: number) {
    const data = response.data ?? response;
    return {
      value: data.content,
      pagination: {
        ...initialPaginationState,
        numberOfElements: data.numberOfElements,
        totalPages: data.totalPages,
        totalElements: data.totalElements,
        first: data.first,
        last: data.last,
        actualPage: page,
        size: data.size
      }
    };
  }
}
